/* istanbul ignore file */
import { FormRow, BigFormRow } from 'framework/ui/layout';
import { DateInput, TextInput, CountryDropdown, ClearAfterUnmountWrapper, POLAND } from 'framework/form';
import { DOCUMENTS_ALIAS } from 'domain/shared/employee';
import PassportNumberInput from 'domain/people/employee/shared/input/PassportNumberInput';
import DocumentsDropdown from './DocumentsDropdown';
import { DocumentScanUploaderInput } from 'domain/shared/employee';
import { Box } from '@mui/material';

type Props = {
  source: string;
  value: any;
};

type DocumentTypeConfig = {
  hasScans: boolean;
  renderNumberInput: (source: string) => React.ReactElement;
  fields: FieldConfig[];
};

type FieldConfig<TProps = any> = {
  component: React.ComponentType<TProps>;
  source: string;
  label: string;
  props?: Partial<TProps>;
};

const DOCUMENT_FIELDS_BY_TYPE: Record<string, DocumentTypeConfig> = {
  [DOCUMENTS_ALIAS.PESEL]: {
    hasScans: false,
    renderNumberInput: (source: string) => <PassportNumberInput source={source} isOnlyNumber />,
    fields: [{ component: CountryDropdown, source: 'documentDetails.citizenship', label: 'Citizenship' }],
  },

  [DOCUMENTS_ALIAS.NIP]: {
    hasScans: false,
    renderNumberInput: (source: string) => <PassportNumberInput source={source} isOnlyNumber />,
    fields: [
      {
        component: CountryDropdown,
        source: 'documentDetails.countryOfRegistration',
        label: 'Country of registration',
        props: {
          priorityCountries: [POLAND.name],
        },
      },
    ],
  },

  [DOCUMENTS_ALIAS.REGON]: {
    hasScans: false,
    renderNumberInput: (source: string) => <PassportNumberInput source={source} isOnlyNumber />,
    fields: [],
  },

  DEFAULT: {
    hasScans: true,
    renderNumberInput: (source: string) => <PassportNumberInput source={source} isOnlyNumber={false} />,
    fields: [
      { component: TextInput, source: 'documentDetails.personalIdNumber', label: 'Identification number' },
      { component: CountryDropdown, source: 'documentDetails.citizenship', label: 'Citizenship' },
      { component: DateInput, source: 'documentDetails.issuedAt', label: 'Date of issue' },
      { component: DateInput, source: 'expiredAt', label: 'Expiry date' },
      { component: TextInput, source: 'documentDetails.issuedBy', label: 'Issued by' },
      { component: TextInput, source: 'documentDetails.surname', label: 'Surname' },
      { component: TextInput, source: 'documentDetails.givenName', label: 'Given name' },
    ],
  },
};

const DocumentInput = ({ source, value }: Props) => {
  const type = value?.type;

  const config = DOCUMENT_FIELDS_BY_TYPE[type] || DOCUMENT_FIELDS_BY_TYPE.DEFAULT;

  if (!type) {
    return (
      <Box width="100%">
        <FormRow>
          <DocumentsDropdown source={`${source}.type`} label="Document type" />
        </FormRow>
      </Box>
    );
  }

  return (
    <Box width="100%">
      <FormRow>
        <DocumentsDropdown source={`${source}.type`} label="Document type" />
      </FormRow>

      <FormRow>
        <ClearAfterUnmountWrapper>{config.renderNumberInput(`${source}`)}</ClearAfterUnmountWrapper>

        {config.fields.map((field, idx) => {
          const Component = field.component;
          return (
            // eslint-disable-next-line react/no-array-index-key
            <ClearAfterUnmountWrapper key={idx}>
              <Component source={`${source}.${field.source}`} label={field.label} {...(field.props || {})} />
            </ClearAfterUnmountWrapper>
          );
        })}
      </FormRow>

      <BigFormRow>
        <ClearAfterUnmountWrapper isVisible={config.hasScans}>
          <DocumentScanUploaderInput label="Document scans" source={`${source}.scansIds`} />
        </ClearAfterUnmountWrapper>
      </BigFormRow>
    </Box>
  );
};

export default DocumentInput;
